import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import clsx from "clsx";
import {
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  TRACK_EVENTS,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntologiesForSelect } from "core/model/utils/ontologies/hooks";
import {
  FileCategory,
  FileShareMode,
  Filev2,
  QueryProgress,
  RecareFileType,
} from "core/types";
import { ConnectedCheckbox } from "ds/ui";
import { ProcessedFile } from "ds_legacy/components/FileUploader";
import RSButton from "ds_legacy/components/RSButton";
import { SelectConnectedWithForm } from "ds_legacy/components/Select";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import TextInputField from "ds_legacy/components/TextInputField";
import { dp, margin } from "ds_legacy/materials/metrics";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { getFileSource } from "dsl/ecosystems/PatientFiles/utils";
import { FileUpIcon, InfoIcon } from "lucide-react";
import {
  SimpleFormRenderProps,
  composeValidation,
  convertModelDefinition,
  singleReactSelectDef,
  validateModel,
  valueDef,
} from "react-forms-state";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import { useDialogStyles } from "..";

type FileInitialValues = {
  category?: { id: number; label: string; value: FileCategory } | null;
  fileName: string | undefined;
  kis_document_id_string: Filev2["kis_document_id_string"] | undefined;
  should_extract_on_upload: boolean;
};
type FileSubmittedValues = {
  category_other?: string;
  category: FileCategory;
  fileName: string;
  kis_document_id_string: Filev2["kis_document_id_string"] | undefined;
  recare_file_type?: RecareFileType;
  share_mode?: FileShareMode;
  should_extract_on_upload?: boolean;
};

export type FileUploadSubmitHandler = (
  data: UploadedFileResult,
  options?: {
    onStarted?: () => void;
    onComplete?: (fileId: number) => Promise<void>;
  },
) => Promise<void>;

export type UploadedFileResult = FileSubmittedValues & ProcessedFile;

export type FileUploadInitialValues = FileInitialValues;

export type FileUploadModalProps = {
  closeModal: () => void;
  handleSubmit: FileUploadSubmitHandler;
  initialValues: FileUploadInitialValues;
  isOpen?: boolean;
  mutationProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
};

const modelDefinition = {
  ...valueDef("should_extract_on_upload", { defaultValue: false }),
  ...valueDef("fileName", { fieldRequired: true }),
  ...singleReactSelectDef("category", { fieldRequired: true }),
};

function CancelButton({
  closeModal,
  initialValues,
  mutationProgress,
  setAction,
}: {
  closeModal: () => void;
  initialValues: FileUploadInitialValues;
  mutationProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
}) {
  const { isDesktop, isMobile } = useMedia();
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  function handleClose() {
    trackEvent({
      cta: "back",
      file_implementation: "HIS",
      modal: "upload",
      name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
      source: getFileSource(initialValues),
    });
    closeModal();
    if (isDesktop) {
      setAction({
        data: {} as FileFeatureData,
        type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
      });
    }
  }

  if (isMobile) {
    return (
      <RSButton
        buttonWrapperStyle={{
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
        color="primary"
        disabled={mutationProgress === QUERY_PROGRESS_PENDING}
        id="cancel"
        loading="na"
        style={{ width: "100%" }}
        variant="outlined"
        onClick={handleClose}
      >
        {translations.actions.cancel}
      </RSButton>
    );
  }

  return (
    <RSButton
      color="primary"
      disabled={mutationProgress === QUERY_PROGRESS_PENDING}
      id="cancel"
      loading="na"
      variant="text"
      onClick={handleClose}
    >
      {translations.actions.back}
    </RSButton>
  );
}

function UploadFileButton({
  initialValues,
  mutationProgress,
  submit,
}: {
  initialValues: FileUploadInitialValues;
  mutationProgress: QueryProgress;
  submit: () => void;
}) {
  const { isMobile } = useMedia();
  const { trackEvent } = useTracking();
  const translations = useTranslations();

  function handleUpload() {
    trackEvent({
      cta: "upload",
      file_implementation: "HIS",
      modal: "upload",
      name: TRACK_EVENTS.FILE_MODAL_CTA_CLICKED,
      source: getFileSource(initialValues),
    });
    submit();
  }

  if (isMobile) {
    return (
      <RSButton
        buttonWrapperStyle={{
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          marginLeft: 0,
          width: "100%",
        }}
        color="primary"
        id="upload"
        LeftIcon={FileUpIcon}
        loading={mutationProgress}
        style={{ width: "100%" }}
        variant="contained"
        onClick={handleUpload}
      >
        {translations.fileSection.fileUploadComponent.uploadFile}
      </RSButton>
    );
  }

  return (
    <RSButton
      color="primary"
      id="upload"
      inverted
      loading={mutationProgress}
      variant="text"
      onClick={handleUpload}
    >
      {translations.actions.uploadFile}
    </RSButton>
  );
}

function FileUploadContent() {
  const isEligiblePage = useIsEligiblePage();

  const classes = useDialogStyles();
  const translations = useTranslations();
  const { isMobile } = useMedia();
  const fileCategoryOntologies = useGetOntologiesForSelect({
    sort: true,
    type: ONTOLOGIES.FILE_CATEGORY,
  });
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  const showExtractCheckbox =
    (isDocsAndExtractActive || isDischargeLetterActive) && !isEligiblePage;

  return (
    <>
      <DialogTitle>
        {translations.fileSection.fileUploadComponent.titleModal}
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <TextInputField
          elementName="fileName"
          inputType={isMobile ? "FULL_WIDTH" : "ISLAND"}
          label={translations.fileSection.fileUploadComponent.fileName}
          placeholder={translations.fileSection.fileUploadComponent.fileName}
          secure={
            translations.fileSection.fileUploadComponent.encryptedFieldSecure
          }
          style={{
            margin: margin(1, 0, 1, 0),
            width: isMobile ? "100%" : "80%",
          }}
        />
        <div style={{ margin: margin(1, 0, 1, 0) }}>
          <SelectConnectedWithForm
            elementName="category"
            hideSelectedOptions={false}
            label={translations.fileSection.fileUploadComponent.fileCategory}
            maxWidth={isMobile ? "100%" : dp(350)}
            minWidth={dp(250)}
            options={fileCategoryOntologies}
            placeholder={
              translations.fileSection.fileUploadComponent.fileCategory
            }
          />
        </div>
        <div
          className={clsx(
            "mt-4 flex flex-col gap-5 rounded-md bg-primary-light p-4",
            {
              hidden: !showExtractCheckbox,
            },
          )}
        >
          <div className="flex flex-col gap-1">
            <h3 className="text-heading-md font-bold">
              {translations.extractDocs.fileUploadModal.extractBannerTitle}
            </h3>
            <p className="text-body-md">
              {
                translations.extractDocs.fileUploadModal
                  .extractBannerDescription
              }
            </p>
            <div className="flex gap-2">
              <InfoIcon className="text-black" />
              <p className="text-body-md">
                {
                  translations.extractDocs.fileUploadModal
                    .extractBannerInformation
                }
              </p>
            </div>
          </div>
          <ConnectedCheckbox
            label={
              translations.extractDocs.fileUploadModal.extractBannerCheckbox
            }
            name="should_extract_on_upload"
          />
        </div>
      </DialogContent>
    </>
  );
}

function FileUploadPending() {
  const classes = useDialogStyles();
  const translations = useTranslations();

  return (
    <>
      <DialogTitle>{translations.general.oneMoment}</DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <DialogContentText>
          {translations.fileSection.events.documentIsBeingUploaded}
        </DialogContentText>
      </DialogContent>
    </>
  );
}

export function FileUploadModal({
  closeModal,
  handleSubmit,
  initialValues,
  isOpen,
  mutationProgress,
  setAction,
}: FileUploadModalProps) {
  const { isMobile } = useMedia();
  const classes = useDialogStyles();

  if (!isOpen || !initialValues.fileName) return null;

  return (
    <SimpleFormRenderProps
      formInputValue={{ ...initialValues }}
      modelDefinition={convertModelDefinition(modelDefinition)}
      validate={composeValidation(
        validateModel(convertModelDefinition(modelDefinition)),
      )}
      onSubmit={(value) => handleSubmit(value)}
    >
      {({ submit }) => (
        <Dialog
          classes={{ paper: classes.dialog }}
          fullScreen={isMobile}
          fullWidth
          maxWidth="sm"
          open={Boolean(isOpen)}
          sx={
            isMobile
              ? {
                  top: `calc(var(--app-bar-height) + var(--tabs-height-mobile))`,
                  zIndex: 0,
                }
              : undefined
          }
        >
          {mutationProgress === QUERY_PROGRESS_NOT_STARTED ? (
            <FileUploadContent />
          ) : (
            <FileUploadPending />
          )}

          <DialogActions
            sx={
              isMobile
                ? { flexDirection: "column", justifyContent: "center" }
                : undefined
            }
          >
            <CancelButton
              closeModal={closeModal}
              initialValues={initialValues}
              mutationProgress={mutationProgress}
              setAction={setAction}
            />
            <UploadFileButton
              initialValues={initialValues}
              mutationProgress={mutationProgress}
              submit={submit}
            />
          </DialogActions>
        </Dialog>
      )}
    </SimpleFormRenderProps>
  );
}
export default FileUploadModal;
